import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Users, ChevronLeft, ChevronRight, UserCircle, ChevronDown } from 'lucide-react'
import { customerService } from '../services/customers'
import type { Customer } from '../../../types'
import { UserRole } from '../../../types'
import { authService } from '../../identity/services/auth'
import { useWindowWidth } from '../../../lib/useWindowWidth'

const PAGE_SIZE = 20

function MobileCustomerCards({
  customers, isAdmin, onNavigate,
}: {
  customers: Customer[]
  isAdmin: boolean
  onNavigate: (id: number) => void
}) {
  const [expandedId, setExpandedId] = useState<number | null>(null)
  return (
    <div className="space-y-2">
      {customers.map(c => {
        const expanded = expandedId === c.id
        return (
          <div key={c.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div
              className="flex items-center gap-2.5 px-3 py-3 cursor-pointer"
              onClick={() => setExpandedId(expanded ? null : c.id)}
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0"
                style={{ background: 'var(--brand-50)', color: 'var(--brand-700)' }}
              >
                {c.name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{c.name}</p>
                <p className="text-xs tabular-nums" style={{ color: 'var(--text-tertiary)' }}>{c.mobile}</p>
              </div>
              <span className={c.is_active ? 'badge-success' : 'badge-neutral'} style={{ fontSize: 10 }}>
                {c.is_active ? 'Active' : 'Merged'}
              </span>
              <ChevronDown
                className="h-4 w-4 shrink-0 transition-transform"
                style={{ transform: expanded ? 'rotate(180deg)' : 'none', color: 'var(--text-tertiary)' }}
              />
            </div>
            {expanded && (
              <div style={{ borderTop: '1px solid var(--border-default)', padding: '10px 12px', background: 'var(--surface-subtle)' }}>
                {c.city && (
                  <div className="flex justify-between text-xs py-1">
                    <span style={{ color: 'var(--text-tertiary)' }}>City</span>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{c.city}</span>
                  </div>
                )}
                {c.email && (
                  <div className="flex justify-between text-xs py-1">
                    <span style={{ color: 'var(--text-tertiary)' }}>Email</span>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{c.email}</span>
                  </div>
                )}
                <div className="flex justify-between text-xs py-1">
                  <span style={{ color: 'var(--text-tertiary)' }}>Since</span>
                  <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                    {new Date(c.created_at).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
                  </span>
                </div>
                {isAdmin && (
                  <div className="flex justify-between text-xs py-1">
                    <span style={{ color: 'var(--text-tertiary)' }}>Tenant</span>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>T-{c.tenant_id}</span>
                  </div>
                )}
                <button
                  className="w-full flex items-center justify-center gap-1.5 text-xs py-2 rounded-md border mt-3"
                  style={{ borderColor: 'var(--brand-primary)', color: 'white', background: 'var(--brand-primary)' }}
                  onClick={e => { e.stopPropagation(); onNavigate(c.id) }}
                >
                  <UserCircle className="h-3.5 w-3.5" /> View Profile
                </button>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default function CustomersPage() {
  const navigate    = useNavigate()
  const currentUser = authService.getCurrentUser()
  const isMobile    = useWindowWidth() < 768

  const [customers, setCustomers] = useState<Customer[]>([])
  const [total, setTotal]         = useState(0)
  const [page, setPage]           = useState(1)
  const [search, setSearch]       = useState('')
  const [loading, setLoading]     = useState(true)
  const [error, setError]         = useState<string | null>(null)

  const isAdmin = currentUser?.role === UserRole.BACKEND_ADMIN

  const fetchCustomers = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params: Record<string, unknown> = { page, page_size: PAGE_SIZE }
      if (search.trim()) params.search = search.trim()
      const data = await customerService.list(params)
      setCustomers(data.items)
      setTotal(data.total)
    } catch {
      setError('Failed to load customers. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [page, search])

  useEffect(() => {
    const t = setTimeout(fetchCustomers, search ? 300 : 0)
    return () => clearTimeout(t)
  }, [fetchCustomers, search])

  const totalPages = Math.ceil(total / PAGE_SIZE)

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="section-header">
        <div>
          <h1 className="page-title">Customers</h1>
          <p className="page-subtitle">
            {total.toLocaleString()} customer{total !== 1 ? 's' : ''} across all vehicles and renewals
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none"
          style={{ color: 'var(--text-tertiary)' }}
        />
        <input
          type="text"
          className="input pl-9"
          placeholder="Search by mobile number…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1) }}
        />
      </div>

      {error && (
        <div className="alert-danger">
          <span>{error}</span>
        </div>
      )}

      {/* Table / Card view */}
      {isMobile ? (
        loading ? (
          <div className="flex justify-center py-10"><div className="spinner" /></div>
        ) : customers.length === 0 ? (
          <div className="empty-state">
            <Users className="h-10 w-10 mb-3" style={{ color: 'var(--text-tertiary)' }} />
            <p className="font-medium" style={{ color: 'var(--text-primary)' }}>No customers found</p>
          </div>
        ) : (
          <MobileCustomerCards customers={customers} isAdmin={isAdmin} onNavigate={id => navigate(`/customers/${id}`)} />
        )
      ) : (
        <div className="table-card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Mobile</th>
                <th className="hidden md:table-cell">Email</th>
                <th className="hidden sm:table-cell">City</th>
                {isAdmin && <th className="hidden lg:table-cell">Tenant</th>}
                <th className="hidden sm:table-cell">Since</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={isAdmin ? 8 : 7} className="text-center py-12">
                    <div className="spinner mx-auto" />
                  </td>
                </tr>
              ) : customers.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 8 : 7}>
                    <div className="empty-state">
                      <Users className="h-10 w-10 mb-3" style={{ color: 'var(--text-tertiary)' }} />
                      <p className="font-medium" style={{ color: 'var(--text-primary)' }}>No customers found</p>
                      <p style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>
                        Customers are created automatically when new leads are added.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                customers.map(c => (
                  <tr
                    key={c.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/customers/${c.id}`)}
                  >
                    <td>
                      <div className="flex items-center gap-2">
                        <UserCircle className="h-5 w-5 shrink-0" style={{ color: 'var(--brand-primary)' }} />
                        <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
                          {c.name}
                        </span>
                      </div>
                    </td>
                    <td style={{ color: 'var(--text-secondary)', fontFamily: 'monospace', fontSize: 13 }}>
                      {c.mobile}
                    </td>
                    <td className="hidden md:table-cell" style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                      {c.email ?? <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                    </td>
                    <td className="hidden sm:table-cell" style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                      {c.city ?? <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                    </td>
                    {isAdmin && (
                      <td className="hidden lg:table-cell" style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>
                        T-{c.tenant_id}
                      </td>
                    )}
                    <td className="hidden sm:table-cell" style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>
                      {new Date(c.created_at).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
                    </td>
                    <td>
                      <span className={c.is_active ? 'badge-success' : 'badge-neutral'}>
                        {c.is_active ? 'Active' : 'Merged'}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn-icon"
                        onClick={e => { e.stopPropagation(); navigate(`/customers/${c.id}`) }}
                        title="View profile"
                      >
                        →
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between" style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
          <span>
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
          </span>
          <div className="flex items-center gap-1">
            <button
              className="btn-icon"
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="px-2">
              {page} / {totalPages}
            </span>
            <button
              className="btn-icon"
              disabled={page === totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
